import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { lopHocAPI, khoiAPI } from '../services/api';
import { toast } from 'react-toastify';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import ImportExcel from '../components/ImportExcel';
import { FiPlus, FiEdit2, FiTrash2, FiUsers, FiUpload, FiDownload, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import * as XLSX from 'xlsx';

const LopHocPage = () => {
  const { t } = useTranslation();
  const [lopHocs, setLopHocs] = useState([]);
  const [khois, setKhois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedLop, setSelectedLop] = useState(null);
  const [formData, setFormData] = useState({ tenLop: '', khoiId: '', moTa: '', buoiHoc: '', gvcn: '', phong: '' });

  useEffect(() => {
    Promise.all([
      fetchLopHocs(),
      fetchKhois()
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLopHocs = async () => {
    try {
      const response = await lopHocAPI.getAll();
      if (response.data.success) {
        setLopHocs(response.data.data);
      }
    } catch (error) {
      toast.error(t('lop.cannotLoad'));
    } finally {
      setLoading(false);
    }
  };

  const fetchKhois = async () => {
    try {
      const response = await khoiAPI.getAll();
      if (response.data.success) {
        setKhois(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching khois:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedLop) {
        await lopHocAPI.update(selectedLop.lop_id, formData);
        toast.success(t('lop.updateSuccess'));
      } else {
        await lopHocAPI.create(formData);
        toast.success(t('lop.addSuccess'));
      }
      setModalOpen(false);
      setSelectedLop(null);
      setFormData({ tenLop: '', khoiId: '', moTa: '', buoiHoc: '', gvcn: '', phong: '' });
      fetchLopHocs();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.errorOccurred'));
    }
  };

  const handleEdit = (lop) => {
    setSelectedLop(lop);
    setFormData({
      tenLop: lop.ten_lop,
      khoiId: lop.khoi_id || '',
      moTa: lop.mo_ta || '',
      buoiHoc: lop.buoi_hoc || '',
      gvcn: lop.gvcn || '',
      phong: lop.phong || ''
    });
    setModalOpen(true);
  };

  const handleRandomBuoi = async () => {
    if (lopHocs.length === 0) return;
    // Nhóm lớp theo khối
    const khoiGroups = new Map();
    for (const lop of lopHocs) {
      const key = lop.khoi_id || 'none';
      if (!khoiGroups.has(key)) khoiGroups.set(key, []);
      khoiGroups.get(key).push(lop);
    }
    // Với mỗi khối: xáo ngẫu nhiên rồi gán xen kẽ Sáng/Chiều
    const updates = [];
    for (const lops of khoiGroups.values()) {
      const shuffled = [...lops].sort(() => Math.random() - 0.5);
      shuffled.forEach((lop, i) => {
        updates.push({ lop, buoiHoc: i % 2 === 0 ? 'Sang' : 'Chieu' });
      });
    }
    try {
      await Promise.all(updates.map(({ lop, buoiHoc }) =>
        lopHocAPI.update(lop.lop_id, {
          tenLop: lop.ten_lop,
          khoiId: lop.khoi_id || '',
          moTa: lop.mo_ta || '',
          buoiHoc,
          gvcn: lop.gvcn || '',
          phong: lop.phong || ''
        })
      ));
      fetchLopHocs();
      toast.success('Đã đặt buổi ngẫu nhiên!');
    } catch {
      toast.error('Lỗi khi đặt buổi!');
    }
  };

  const handleDelete = (lop) => {
    setSelectedLop(lop);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await lopHocAPI.delete(selectedLop.lop_id);
      toast.success(t('lop.deleteSuccess'));
      setConfirmOpen(false);
      setSelectedLop(null);
      fetchLopHocs();
    } catch (error) {
      toast.error(error.response?.data?.message || t('lop.cannotDelete'));
    }
  };

  const openAddModal = () => {
    setSelectedLop(null);
    setFormData({ tenLop: '', khoiId: '', moTa: '', buoiHoc: '', gvcn: '', phong: '' });
    setModalOpen(true);
  };

  const handleExportExcel = () => {
    const header = ['Tên lớp', 'Khối', 'Mô tả'];
    const rows = lopHocs.map(l => [l.ten_lop, l.ten_khoi || '', l.mo_ta || '']);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lớp học');
    XLSX.writeFile(wb, 'lop_hoc.xlsx');
  };

  const handleImport = async (file) => {
    const response = await lopHocAPI.import(file);
    if (response.data.success) {
      fetchLopHocs();
      toast.success(response.data.message);
    }
    return response;
  };

  // Sắp xếp thứ tự
  const moveUp = async (lopHoc, index) => {
    if (index === 0) return; // Đã ở vị trí đầu
      const updated = [...lopHocs];
      const [item] = updated.splice(index, 1);
      updated.splice(index - 1, 0, item);

      try {
        await lopHocAPI.reorder(updated.map((lop) => lop.lop_id));
        fetchLopHocs();
        toast.success(t('lop.orderUpdated'));
      } catch (error) {
        console.error('Error reordering lớp học:', error);
        toast.error(t('common.cannotUpdateOrder'));
      }
  };

  const moveDown = async (lopHoc, index) => {
    if (index === lopHocs.length - 1) return; // Đã ở vị trí cuối
      const updated = [...lopHocs];
      const [item] = updated.splice(index, 1);
      updated.splice(index + 1, 0, item);

      try {
        await lopHocAPI.reorder(updated.map((lop) => lop.lop_id));
        fetchLopHocs();
        toast.success(t('lop.orderUpdated'));
      } catch (error) {
        console.error('Error reordering lớp học:', error);
        toast.error(t('common.cannotUpdateOrder'));
      }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2>{t('lop.pageTitle')} ({lopHocs.length})</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-secondary" onClick={handleRandomBuoi} title="Tự động gán Sáng/Chiều cân bằng trong từng khối">
              Đặt buổi ngẫu nhiên
            </button>
            <button className="btn btn-secondary" onClick={handleExportExcel}>
              <FiDownload /> Export Excel
            </button>
            <button className="btn btn-secondary" onClick={() => setImportOpen(true)}>
              <FiUpload /> {t('lop.importBtn')}
            </button>
            <button className="btn btn-primary" onClick={openAddModal}>
              <FiPlus /> {t('lop.addBtn')}
            </button>
          </div>
        </div>
        <div className="card-body">
          {lopHocs.length === 0 ? (
            <div className="empty-state">
              <FiUsers />
              <h3>{t('lop.empty')}</h3>
              <p>{t('lop.emptyHint')}</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('lop.colOrder')}</th>
                    <th>{t('lop.colNo')}</th>
                    <th>{t('lop.colName')}</th>
                    <th>{t('lop.colKhoi')}</th>
                    <th>Buổi học</th>
                    <th>GVCN</th>
                    <th>Phòng</th>
                    <th>{t('lop.colDesc')}</th>
                    <th>{t('lop.colCreated')}</th>
                    <th>{t('lop.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {lopHocs.map((lop, index) => (
                    <tr key={lop.lop_id}>
                      <td>
                        <div className="order-controls">
                          <button
                            className="btn btn-secondary btn-icon btn-sm"
                            onClick={() => moveUp(lop, index)}
                            disabled={index === 0}
                            title={t('common.moveUp')}
                          >
                            <FiChevronUp />
                          </button>
                          <button
                            className="btn btn-secondary btn-icon btn-sm"
                            onClick={() => moveDown(lop, index)}
                            disabled={index === lopHocs.length - 1}
                            title={t('common.moveDown')}
                          >
                            <FiChevronDown />
                          </button>
                        </div>
                      </td>
                      <td>{index + 1}</td>
                      <td><strong>{lop.ten_lop}</strong></td>
                      <td>{lop.ten_khoi || '-'}</td>
                      <td>
                        {lop.buoi_hoc === 'Sang' ? '☀️ Sáng' : lop.buoi_hoc === 'Chieu' ? '🌙 Chiều' : '-'}
                      </td>
                      <td>{lop.gvcn || '-'}</td>
                      <td>{lop.phong || '-'}</td>
                      <td>{lop.mo_ta || '-'}</td>
                      <td>{new Date(lop.created_at).toLocaleDateString('vi-VN')}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="btn btn-secondary btn-icon"
                            onClick={() => handleEdit(lop)}
                            title={t('common.edit')}
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            className="btn btn-danger btn-icon"
                            onClick={() => handleDelete(lop)}
                            title={t('common.delete')}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal thêm/sửa */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedLop ? t('lop.modalEditTitle') : t('lop.modalAddTitle')}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" onClick={handleSubmit}>
              {selectedLop ? t('common.update') : t('common.addNew')}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t('lop.fieldName')}</label>
            <input
              type="text"
              className="form-control"
              value={formData.tenLop}
              onChange={(e) => setFormData({ ...formData, tenLop: e.target.value })}
              placeholder={t('lop.fieldNamePlaceholder')}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('lop.fieldKhoi')}</label>
            <select
              className="form-control"
              value={formData.khoiId}
              onChange={(e) => setFormData({ ...formData, khoiId: e.target.value })}
              required
            >
              <option value="">{t('lop.selectKhoi')}</option>
              {khois.map(khoi => (
                <option key={khoi.khoi_id} value={khoi.khoi_id}>
                  {khoi.ten_khoi}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Buổi học</label>
            <select
              className="form-control"
              value={formData.buoiHoc}
              onChange={(e) => setFormData({ ...formData, buoiHoc: e.target.value })}
            >
              <option value="">-- Chưa xác định --</option>
              <option value="Sang">☀️ Sáng</option>
              <option value="Chieu">🌙 Chiều</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Giáo viên chủ nhiệm</label>
              <input
                type="text"
                className="form-control"
                value={formData.gvcn}
                onChange={(e) => setFormData({ ...formData, gvcn: e.target.value })}
                placeholder="Tên giáo viên chủ nhiệm"
              />
            </div>
            <div className="form-group" style={{ flex: '0 0 120px' }}>
              <label>Phòng</label>
              <input
                type="text"
                className="form-control"
                value={formData.phong}
                onChange={(e) => setFormData({ ...formData, phong: e.target.value })}
                placeholder="VD: P.101"
              />
            </div>
          </div>
          <div className="form-group">
            <label>{t('lop.fieldDesc')}</label>
            <textarea
              className="form-control"
              value={formData.moTa}
              onChange={(e) => setFormData({ ...formData, moTa: e.target.value })}
              placeholder={t('lop.fieldDescPlaceholder')}
              rows={3}
            />
          </div>
        </form>
      </Modal>

      {/* Confirm xóa */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title={t('lop.confirmDeleteTitle')}
        message={t('lop.confirmDeleteMsg', { name: selectedLop?.ten_lop })}
      />

      {/* Import Excel */}
      <ImportExcel
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImport}
        title={t('lop.importTitle')}
        templateColumns={['Tên lớp', 'Khối', 'Mô tả']}
        templateData={[
          ['10A1', 'Khối 10', 'Lớp chuyên Toán'],
          ['11B2', 'Khối 11', 'Lớp thường'],
          ['12C3', 'Khối 12', '']
        ]}
        templateFileName="mau_lop_hoc.xlsx"
      />
    </div>
  );
};

export default LopHocPage;
